import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * Horizontal ticker strip (Yoshi).
 *
 * Clones the item group until the track is wider than the viewport plus one group, then scrolls the
 * track by exactly one group width per loop so the seam is invisible. With reduced motion the clones
 * are never made and CSS wraps the items as a static, centered row.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track - The moving track.
 * @property {HTMLElement} group - The original list of items.
 *
 * @extends Component<Refs>
 */
class PlusTicker extends Component {
  requiredRefs = ['track', 'group'];

  /** @type {MediaQueryList | null} */
  #motionQuery = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  #lastViewportWidth = 0;
  #lastGroupWidth = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#motionQuery.addEventListener('change', this.#build);

    this.#resizeObserver = new ResizeObserver(this.#handleResize);
    this.#resizeObserver.observe(this);
    this.#resizeObserver.observe(this.refs.group);

    document.addEventListener('shopify:block:select', this.#handleBlockSelect);
    document.addEventListener('shopify:block:deselect', this.#handleBlockDeselect);

    this.#build();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#motionQuery?.removeEventListener('change', this.#build);
    this.#resizeObserver?.disconnect();
    this.#handleResize.cancel?.();
    document.removeEventListener('shopify:block:select', this.#handleBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.#handleBlockDeselect);
  }

  #handleResize = debounce(() => {
    const viewportWidth = this.clientWidth;
    const groupWidth = this.refs.group.getBoundingClientRect().width;

    if (viewportWidth === this.#lastViewportWidth && groupWidth === this.#lastGroupWidth) return;

    this.#build();
  }, 150);

  #build = () => {
    const { track, group } = this.refs;

    for (const clone of track.querySelectorAll('[data-clone]')) clone.remove();
    this.removeAttribute('data-running');

    const viewportWidth = this.clientWidth;
    const groupWidth = group.getBoundingClientRect().width;

    this.#lastViewportWidth = viewportWidth;
    this.#lastGroupWidth = groupWidth;

    if (this.#motionQuery?.matches || !groupWidth || !viewportWidth) return;

    // One group must always be off-screen so the loop restarts without a visible jump.
    const copies = Math.ceil(viewportWidth / groupWidth) + 1;

    for (let i = 0; i < copies; i++) {
      const clone = /** @type {HTMLElement} */ (group.cloneNode(true));
      clone.removeAttribute('ref');
      clone.setAttribute('data-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.inert = true;
      for (const node of clone.querySelectorAll('[id]')) node.removeAttribute('id');
      for (const node of clone.querySelectorAll('[data-shopify-editor-block]')) {
        node.removeAttribute('data-shopify-editor-block');
      }
      track.appendChild(clone);
    }

    const speed = Math.max(Number(this.dataset.speed) || 50, 1);

    this.style.setProperty('--plus-ticker-distance', `${groupWidth}px`);
    this.style.setProperty('--plus-ticker-duration', `${(groupWidth / speed).toFixed(2)}s`);
    this.setAttribute('data-running', '');
  };

  /** @param {Event} event */
  #handleBlockSelect = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) {
      this.setAttribute('data-paused', '');
    }
  };

  /** @param {Event} event */
  #handleBlockDeselect = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) {
      this.removeAttribute('data-paused');
    }
  };
}

if (!customElements.get('plus-ticker')) {
  customElements.define('plus-ticker', PlusTicker);
}
